import React, { FC, Fragment, ReactNode } from 'react';
import styled from 'styled-components';
import { T } from 'src/shared/helpers';
import { SettingsGroup, SettingsPage, SettingsToggleRow } from './sceneEditorSettingsLayout';

// LE SCORCIATOIE DA TASTIERA, SCRITTE IN UN POSTO SOLO (utente, 2026-10-22). Erano sparse nei tooltip («Close · Esc»,
// «⌥click to isolate») o non scritte da nessuna parte. È l'elenco di ciò che i comandi fanno già: chi aggiunge un
// tasto lo aggiunge anche qui.

export type KeyboardHost = 'sceneEditor' | 'composer';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
/** Il tasto dei comandi come lo si chiama su questo computer: ⌘ sul Mac, Ctrl altrove. */
export const MOD = isMac ? '⌘' : 'Ctrl';
const ALT = isMac ? '⌥' : 'Alt';
const SHIFT = isMac ? '⇧' : 'Shift';

const Shortcuts = styled.dl`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 2px 16px;
    margin: 0;
    padding: 4px 12px;
    font-size: 12px;

    dt,
    dd {
        display: flex;
        align-items: center;
        min-height: 28px;
        margin: 0;
    }

    dt {
        color: var(--editor-fg, #262a30);
    }

    dd {
        justify-content: flex-end;
        gap: 4px;
        color: var(--editor-muted, #55595f);
    }
`;

// Un tasto: il riquadro col filo, come sulla tastiera.
const Key = styled.kbd`
    display: inline-flex;
    min-width: 22px;
    height: 22px;
    align-items: center;
    justify-content: center;
    padding: 0 6px;
    border: 1px solid var(--editor-line, #d1d3d8);
    border-bottom-width: 2px;
    border-radius: 5px;
    background: var(--editor-panel, #ffffff);
    color: var(--editor-fg, #262a30);
    font: inherit;
    font-size: 11px;
    font-weight: 500;
`;

type Shortcut = { keys: string[][]; label: string; hosts?: KeyboardHost[] };

// `keys` sono le alternative; ogni alternativa è la combinazione di tasti da premere insieme.
const Combo: FC<{ keys: string[][] }> = ({ keys }) => (
    <>
        {keys.map((combo, index) => (
            <Fragment key={combo.join('+')}>
                {index > 0 && <span>{T._('or', 'Admin')}</span>}
                {combo.map((key) => (
                    <Key key={key}>{key}</Key>
                ))}
            </Fragment>
        ))}
    </>
);

const ShortcutGroup: FC<{ title: ReactNode; host: KeyboardHost; items: Shortcut[]; children?: ReactNode }> = ({ title, host, items, children }) => {
    const shown = items.filter((item) => !item.hosts || item.hosts.includes(host));
    if (!shown.length) return null;
    return (
        <SettingsGroup title={title}>
            {children}
            <Shortcuts>
                {shown.map((item) => (
                    <Fragment key={item.label}>
                        <dt>{item.label}</dt>
                        <dd>
                            <Combo keys={item.keys} />
                        </dd>
                    </Fragment>
                ))}
            </Shortcuts>
        </SettingsGroup>
    );
};

export const SceneEditorKeyboardPage: FC<{ host: KeyboardHost; singleKeyShortcuts: boolean; onSingleKeyShortcutsChange: (on: boolean) => void }> = ({
    host,
    singleKeyShortcuts,
    onSingleKeyShortcutsChange
}) => (
    <SettingsPage title={T._('Keyboard', 'Admin')} lead={T._('Every command you can reach from the keyboard. Saved for you, right away.', 'Admin')}>
        <ShortcutGroup
            title={T._('General', 'Admin')}
            host={host}
            items={[
                { keys: [['Tab'], [SHIFT, 'Tab']], label: T._('Move to the next or previous area', 'Admin') },
                { keys: [['Esc']], label: T._('Close a window or a menu', 'Admin') },
                { keys: [[MOD, '/']], label: T._('Open this list', 'Admin') }
            ]}
        />
        <ShortcutGroup
            title={T._('Lists', 'Admin')}
            host={host}
            items={[
                { keys: [['↑'], ['↓']], label: T._('Previous or next row', 'Admin') },
                { keys: [['Home'], ['End']], label: T._('First or last row', 'Admin') },
                { keys: [['→']], label: T._('Open a group, or go to its first row', 'Admin') },
                { keys: [['←']], label: T._('Close a group, or go to its parent', 'Admin') },
                { keys: [['Enter'], ['Space']], label: T._('Select the row and show its properties', 'Admin') },
                { keys: [['Enter']], label: T._('On a selected row: open it, like a double click', 'Admin'), hosts: ['composer'] },
                { keys: [[MOD, 'Space']], label: T._('Add the row to the selection, or remove it', 'Admin') },
                { keys: [[MOD, 'A']], label: T._('Select every row shown', 'Admin'), hosts: ['sceneEditor'] },
                { keys: [['F2']], label: T._('Rename the row', 'Admin'), hosts: ['sceneEditor'] },
                {
                    keys: [
                        [ALT, '↑'],
                        [ALT, '↓']
                    ],
                    label: T._('Move the row up or down', 'Admin')
                },
                { keys: [['Tab']], label: T._('Reach the commands of the row, then leave the list', 'Admin') }
            ]}
        />
        <ShortcutGroup
            title={T._('Menus', 'Admin')}
            host={host}
            items={[
                { keys: [['↑'], ['↓']], label: T._('Previous or next command', 'Admin') },
                { keys: [['Home'], ['End']], label: T._('First or last command', 'Admin') },
                { keys: [['Enter']], label: T._('Run the command', 'Admin') },
                { keys: [['Tab']], label: T._('Close the menu and move on', 'Admin') }
            ]}
        />
        <ShortcutGroup
            title={T._('Stage', 'Admin')}
            host={host}
            items={[
                { keys: [['V']], label: T._('Select', 'Admin'), hosts: ['sceneEditor'] },
                { keys: [['W']], label: T._('Move', 'Admin'), hosts: ['sceneEditor'] },
                { keys: [['E']], label: T._('Rotate', 'Admin'), hosts: ['sceneEditor'] },
                { keys: [['R']], label: T._('Scale', 'Admin'), hosts: ['sceneEditor'] },
                { keys: [['F']], label: T._('Frame the selection', 'Admin'), hosts: ['sceneEditor'] }
            ]}
        >
            <SettingsToggleRow
                id='settings-single-key-shortcuts'
                label={T._('Single-key shortcuts', 'Admin')}
                sub={T._('Turn them off if you use voice control or a screen reader: a single letter can start a tool by mistake.', 'Admin')}
                checked={singleKeyShortcuts}
                onChange={onSingleKeyShortcutsChange}
            />
        </ShortcutGroup>
        <ShortcutGroup
            title={T._('Fields', 'Admin')}
            host={host}
            items={[
                { keys: [['↑'], ['↓']], label: T._('Change a number by one step', 'Admin') },
                { keys: [['Enter']], label: T._('Confirm the value', 'Admin') },
                { keys: [['Esc']], label: T._('Go back to the previous value', 'Admin') }
            ]}
        />
        <ShortcutGroup
            title={T._('Panels', 'Admin')}
            host={host}
            items={[
                { keys: [['←'], ['→']], label: T._('On the edge of a column: make it narrower or wider', 'Admin') },
                {
                    keys: [
                        [SHIFT, '←'],
                        [SHIFT, '→']
                    ],
                    label: T._('The same, in larger steps', 'Admin'),
                    hosts: ['sceneEditor']
                }
            ]}
        />
    </SettingsPage>
);
